// src/utils/mockDataRFQ.ts
// Step 9 — Supplier RFQ Portal
// Requests for quotation, supplier responses, award workflow

// ── Types ─────────────────────────────────────────────────────────

export type RFQStatus =
  | 'DRAFT'               // being prepared
  | 'SENT'                // sent to suppliers, awaiting responses
  | 'RESPONSES_RECEIVED'  // one or more responses in
  | 'AWARDED'             // winner selected, PO created
  | 'CANCELLED'

export type RFQComplianceStatus = 'NOT_CHECKED' | 'COMPLIANT' | 'NEEDS_REVIEW' | 'NON_COMPLIANT' | 'BLOCKED'

export interface RFQLineItem {
  id: string
  lineNo: number
  description: string
  qty: number
  unit: string
  specReference?: string         // MRTS or internal spec (display)
  notes?: string
  // Phase 1 FK — real link to canonical BOM line
  bomLineId?: string | null       // CanonicalBOMLine.id
  complianceStatus?: RFQComplianceStatus  // derived from bomLine.complianceFlag
}

export interface QuoteLineResponse {
  rfqLineId: string
  unitPriceExGST: number
  totalExGST: number
  leadTimeDays: number
  available: boolean       // can this line be supplied?
  alternativeDescription?: string
  notes?: string
}

export interface SupplierQuoteResponse {
  id: string
  rfqId: string
  supplierId: string
  supplierName: string
  submittedAt: string
  validUntil: string
  lines: QuoteLineResponse[]
  subtotalExGST: number
  gst: number
  totalIncGST: number
  paymentTermsDays: number
  deliveryLeadDays: number
  qualifications?: string
  isAwarded: boolean
  rank?: number            // 1 = lowest price
}

export interface RFQ {
  id: string
  reference: string        // RFQ-2026-001
  title: string
  jobId: string
  jobRef: string           // TP-2026-XXX
  description: string
  status: RFQStatus
  lineItems: RFQLineItem[]
  invitedSupplierIds: string[]
  invitedSupplierNames: string[]
  closingDate: string
  deliveryDate?: string
  deliveryAddress?: string
  deliveryType?: 'WAREHOUSE' | 'SITE_DIRECT'  // Phase 1 — determines who records receipt
  responses: SupplierQuoteResponse[]
  awardedSupplierId?: string
  awardedQuoteId?: string
  awardedPORef?: string            // now set to real PO.poNumber on award
  awardedAt?: string
  awardedByName?: string
  complianceGateStatus?: RFQComplianceStatus  // 'COMPLIANT' before SENT is allowed
  complianceCheckedAt?: string
  createdById: string
  createdByName: string
  createdAt: string
  updatedAt: string
  sentAt?: string
  sentByName?: string
}

// ── Status metadata ────────────────────────────────────────────────

export const RFQ_STATUS_META: Record<RFQStatus, { label: string; bg: string; text: string; icon: string }> = {
  DRAFT:              { label: 'Draft',               bg: 'bg-gray-100',   text: 'text-gray-600',   icon: '○' },
  SENT:               { label: 'Sent — awaiting',     bg: 'bg-blue-100',   text: 'text-blue-700',   icon: '→' },
  RESPONSES_RECEIVED: { label: 'Responses received',  bg: 'bg-amber-100',  text: 'text-amber-700',  icon: '⚑' },
  AWARDED:            { label: 'Awarded',             bg: 'bg-green-100',  text: 'text-green-700',  icon: '✓' },
  CANCELLED:          { label: 'Cancelled',           bg: 'bg-gray-100',   text: 'text-gray-400',   icon: '✕' },
}

// ── Mock RFQs ─────────────────────────────────────────────────────

export const MOCK_RFQS: RFQ[] = [

  // RFQ-2026-001: AWARDED — conduit and fittings supply for Maroochydore
  {
    id: 'rfq-001',
    reference: 'RFQ-2026-001',
    title: 'UPVC conduit, cable pits and fittings — TP-2026-003',
    jobId: 'j3', jobRef: 'TP-2026-003',
    description: 'Supply of UPVC 100mm conduit, trafficable cable pits, and associated fittings for the Maroochydore Rd Intersection Reconstruction. Delivery to site in staged lots per construction programme.',
    status: 'AWARDED',
    lineItems: [
      { id:'rl-001-1', lineNo:1, description:'UPVC 100mm conduit — 6m lengths', qty:540, unit:'m', specReference:'MRTS91 cl.4.2', notes:'Qty subject to final survey — +/- 10% variation' },
      { id:'rl-001-2', lineNo:2, description:'Cable pit UPVC 300×450 trafficable lid (Humes or equiv.)', qty:18, unit:'each', specReference:'MRTS91 cl.4.5' },
      { id:'rl-001-3', lineNo:3, description:'UPVC 100mm 90° bend (long radius)', qty:40, unit:'each' },
      { id:'rl-001-4', lineNo:4, description:'UPVC 100mm male adaptor and cap', qty:40, unit:'each' },
    ],
    invitedSupplierIds: ['sup-saferoads','sup-iplex','sup-tradelink'],
    invitedSupplierNames: ['Saferoads Queensland','Iplex Pipelines','Tradelink'],
    closingDate: '2026-04-15',
    deliveryDate: '2026-07-01',
    deliveryAddress: 'Maroochydore Rd site — 148 Maroochydore Rd, Maroochydore QLD 4558',
    responses: [
      {
        id:'qr-001-1', rfqId:'rfq-001', supplierId:'sup-saferoads', supplierName:'Saferoads Queensland',
        submittedAt:'2026-04-12T14:00:00Z', validUntil:'2026-07-12',
        lines:[
          { rfqLineId:'rl-001-1', unitPriceExGST:6.40,   totalExGST:3_456,  leadTimeDays:3,  available:true },
          { rfqLineId:'rl-001-2', unitPriceExGST:245.00, totalExGST:4_410,  leadTimeDays:5,  available:true },
          { rfqLineId:'rl-001-3', unitPriceExGST:12.80,  totalExGST:512,    leadTimeDays:3,  available:true },
          { rfqLineId:'rl-001-4', unitPriceExGST:8.20,   totalExGST:328,    leadTimeDays:3,  available:true },
        ],
        subtotalExGST:8_706, gst:870.60, totalIncGST:9_576.60,
        paymentTermsDays:21, deliveryLeadDays:3,
        qualifications:'Prices valid 90 days. Staged delivery at no extra cost within Sunshine Coast region.',
        isAwarded:true, rank:1,
      },
      {
        id:'qr-001-2', rfqId:'rfq-001', supplierId:'sup-iplex', supplierName:'Iplex Pipelines',
        submittedAt:'2026-04-14T09:00:00Z', validUntil:'2026-06-14',
        lines:[
          { rfqLineId:'rl-001-1', unitPriceExGST:6.10,   totalExGST:3_294,  leadTimeDays:7,  available:true },
          { rfqLineId:'rl-001-2', unitPriceExGST:258.00, totalExGST:4_644,  leadTimeDays:10, available:true },
          { rfqLineId:'rl-001-3', unitPriceExGST:11.90,  totalExGST:476,    leadTimeDays:7,  available:true },
          { rfqLineId:'rl-001-4', unitPriceExGST:7.80,   totalExGST:312,    leadTimeDays:7,  available:true },
        ],
        subtotalExGST:8_726, gst:872.60, totalIncGST:9_598.60,
        paymentTermsDays:30, deliveryLeadDays:7,
        qualifications:'Min order 200m for conduit. Delivery ex-depot. Client to arrange site crane for pits >150kg.',
        isAwarded:false, rank:2,
      },
    ],
    awardedSupplierId:'sup-saferoads', awardedQuoteId:'qr-001-1',
    awardedPORef:'PO-TP003-002',
    awardedAt:'2026-04-16T10:00:00Z', awardedByName:'Jane Smith',
    createdById:'u4', createdByName:'Sarah Lee',
    createdAt:'2026-04-05T09:00:00Z', updatedAt:'2026-04-16T10:00:00Z',
    sentAt:'2026-04-05T12:00:00Z', sentByName:'Sarah Lee',
  },

  // RFQ-2026-002: RESPONSES_RECEIVED — 3C+E cable supply comparison
  {
    id: 'rfq-002',
    reference: 'RFQ-2026-002',
    title: '3C+E power cable supply — TP-2026-003 stage 2',
    jobId: 'j3', jobRef: 'TP-2026-003',
    description: 'Supply of 3C+E power cables for Stage 2 Maroochydore Rd signal installation. Copper surcharge mechanism must be explicitly quoted — base rate + surcharge formula required.',
    status: 'RESPONSES_RECEIVED',
    lineItems: [
      { id:'rl-002-1', lineNo:1, description:'3C+E 6mm² Cu/XLPE/PVC 0.6/1kV (Olex or equiv.)', qty:640, unit:'m', specReference:'MRTS256 cl.3.1', notes:'Must be TMR TAER listed cable' },
      { id:'rl-002-2', lineNo:2, description:'3C+E 16mm² Cu/XLPE/PVC 0.6/1kV (Olex or equiv.)', qty:120, unit:'m', specReference:'MRTS256 cl.3.1' },
      { id:'rl-002-3', lineNo:3, description:'1C 2.5mm² TPS multicore switch cable', qty:200, unit:'m' },
    ],
    invitedSupplierIds: ['sup-olex','sup-nexans','sup-cablex'],
    invitedSupplierNames: ['Prysmian Group (Olex)','Nexans Australia','CableX Direct'],
    closingDate: '2026-06-30',
    deliveryDate: '2026-08-01',
    deliveryAddress: 'Maroochydore Rd site — 148 Maroochydore Rd, Maroochydore QLD 4558',
    responses: [
      {
        id:'qr-002-1', rfqId:'rfq-002', supplierId:'sup-olex', supplierName:'Prysmian Group (Olex)',
        submittedAt:'2026-06-22T11:00:00Z', validUntil:'2026-08-22',
        lines:[
          { rfqLineId:'rl-002-1', unitPriceExGST:16.80, totalExGST:10_752, leadTimeDays:5, available:true, notes:'Includes current copper surcharge +$1.90/m. Base rate $14.90/m.' },
          { rfqLineId:'rl-002-2', unitPriceExGST:38.50, totalExGST:4_620,  leadTimeDays:5, available:true, notes:'Includes copper surcharge. Base rate $34.20/m.' },
          { rfqLineId:'rl-002-3', unitPriceExGST:4.20,  totalExGST:840,    leadTimeDays:3, available:true },
        ],
        subtotalExGST:16_212, gst:1_621.20, totalIncGST:17_833.20,
        paymentTermsDays:30, deliveryLeadDays:5,
        qualifications:'Copper surcharge reviewed monthly. Current surcharge applies at time of order. Full drum only — minimum 250m per SKU.',
        isAwarded:false, rank:1,
      },
      {
        id:'qr-002-2', rfqId:'rfq-002', supplierId:'sup-nexans', supplierName:'Nexans Australia',
        submittedAt:'2026-06-24T15:00:00Z', validUntil:'2026-09-24',
        lines:[
          { rfqLineId:'rl-002-1', unitPriceExGST:17.40, totalExGST:11_136, leadTimeDays:10, available:true, notes:'Nexans Exzhellent range. LSZH outer sheath. Copper surcharge included at current LME rate.' },
          { rfqLineId:'rl-002-2', unitPriceExGST:39.80, totalExGST:4_776,  leadTimeDays:10, available:true },
          { rfqLineId:'rl-002-3', unitPriceExGST:3.90,  totalExGST:780,    leadTimeDays:7,  available:true, notes:'TPS cable — 7-day lead time from Melbourne warehouse.' },
        ],
        subtotalExGST:16_692, gst:1_669.20, totalIncGST:18_361.20,
        paymentTermsDays:30, deliveryLeadDays:10,
        qualifications:'Prices fixed for 60 days from quote date. LSZH sheath adds fire performance advantage. 10% premium vs XLPE/PVC.',
        isAwarded:false, rank:2,
      },
    ],
    createdById:'u4', createdByName:'Sarah Lee',
    createdAt:'2026-06-10T09:00:00Z', updatedAt:'2026-06-24T15:00:00Z',
    sentAt:'2026-06-10T12:00:00Z', sentByName:'Jane Smith',
  },

  // RFQ-2026-003: SENT — PPE bulk order, closes in 3 days
  {
    id: 'rfq-003',
    reference: 'RFQ-2026-003',
    title: 'Annual PPE supply — all sites 2026–27',
    jobId: 'j1', jobRef: 'TP-2026-001',
    description: 'Annual PPE supply agreement for MCB CE operations across all active sites. Seeking pricing for a 12-month supply arrangement to replace current spot-rate purchasing from Blackwoods.',
    status: 'SENT',
    lineItems: [
      { id:'rl-003-1', lineNo:1, description:'Steel cap boot (Blundstone 8" Class 1)', qty:24, unit:'pairs', notes:'Annual estimate — various sizes. Exact size breakdown TBC.' },
      { id:'rl-003-2', lineNo:2, description:'Hi-vis vest Class D (HVSQ compliant)', qty:60, unit:'each' },
      { id:'rl-003-3', lineNo:3, description:'Hard hat Type 1 vented (EN397)', qty:20, unit:'each' },
      { id:'rl-003-4', lineNo:4, description:'HV insulating gloves Class 2 (IEC 60903)', qty:12, unit:'pairs', specReference:'AS/NZS 2225', notes:'Safety critical — must be certified to IEC 60903' },
      { id:'rl-003-5', lineNo:5, description:'Cut-resistant gloves Level 5 (pack 12)', qty:8, unit:'packs' },
    ],
    invitedSupplierIds: ['sup-blackwoods','sup-protector','sup-safety-direct'],
    invitedSupplierNames: ['Blackwoods Australia','Protector Alsafe','Safety Direct QLD'],
    closingDate: '2026-06-28',
    responses: [],
    createdById:'u2', createdByName:'Jane Smith',
    createdAt:'2026-06-14T09:00:00Z', updatedAt:'2026-06-14T12:00:00Z',
    sentAt:'2026-06-14T12:00:00Z', sentByName:'Jane Smith',
  },

  // RFQ-2026-004: DRAFT — concrete supply for TP-2026-003
  {
    id: 'rfq-004',
    reference: 'RFQ-2026-004',
    title: 'Ready-mix concrete supply — TP-2026-003 foundations',
    jobId: 'j3', jobRef: 'TP-2026-003',
    description: 'Supply of ready-mix concrete for signal pole foundations — 6 foundations, estimated 3m³ total. Concrete must comply with MRTS92 and be supplied with NATA batch test certificates.',
    status: 'DRAFT',
    lineItems: [
      { id:'rl-004-1', lineNo:1, description:'Ready-mix concrete Grade 40 20mm aggregate', qty:3.0, unit:'m³', specReference:'MRTS92 cl.4', notes:'NATA batch test certificate required with each delivery. Slump 70–100mm.' },
      { id:'rl-004-2', lineNo:2, description:'Concrete pump hire (if required)', qty:1, unit:'day', notes:'Optional — quote separately. May be substituted with crane bucket.' },
    ],
    invitedSupplierIds: ['sup-boral','sup-hanson'],
    invitedSupplierNames: ['Boral Construction Materials','Hanson Concrete'],
    closingDate: '2026-07-10',
    deliveryDate: '2026-08-15',
    responses: [],
    createdById:'u4', createdByName:'Sarah Lee',
    createdAt:'2026-06-23T14:00:00Z', updatedAt:'2026-06-23T14:00:00Z',
  },
]

// ── Helpers ────────────────────────────────────────────────────────

export function getDaysUntilClosing(rfq: RFQ): number {
  return Math.ceil((new Date(rfq.closingDate).getTime() - Date.now()) / 86_400_000)
}

export function getLowestQuote(rfq: RFQ): SupplierQuoteResponse | undefined {
  if (rfq.responses.length === 0) return undefined
  return [...rfq.responses].sort((a, b) => a.subtotalExGST - b.subtotalExGST)[0]
}

export function getSavingVsHighest(rfq: RFQ): number {
  if (rfq.responses.length < 2) return 0
  const sorted = [...rfq.responses].sort((a, b) => a.subtotalExGST - b.subtotalExGST)
  return sorted[sorted.length - 1].subtotalExGST - sorted[0].subtotalExGST
}
